// useBudgetStore.ts
// Manages budget calculations and data for trip locations

import { create } from "zustand";
import {
  Trip,
  Location,
  ItineraryItem,
  LocationBudget,
  BookingStatus,
} from "@/types";
import useTripStore from "./useTrip";
import { BudgetBreakdown, ParticipantBudget } from "@/types/booking";

interface BudgetStore {
  // Current location budget data
  currentLocationId: string | null;
  locationBudget: LocationBudget;
  budgetBreakdown: BudgetBreakdown;
  participantBudgets: ParticipantBudget[];

  // Actions
  setCurrentLocationId: (locationId: string | null) => void;
  calculateBudget: (locationId: string) => void;
  getBudgetBreakdown: (locationId: string) => BudgetBreakdown;
  getParticipantBudgets: (locationId: string) => ParticipantBudget[];
  updateBudgetCategory: (
    category: keyof LocationBudget,
    amount: number
  ) => void;
}

const useBudgetStore = create<BudgetStore>((set, get) => ({
  currentLocationId: null,
  locationBudget: {
    total: 0,
    accommodation: 0,
    transport: 0,
    activities: 0,
  },
  budgetBreakdown: {
    accommodation: [],
    transport: [],
    activities: [],
  },
  participantBudgets: [],

  setCurrentLocationId: (locationId: string | null) => {
    set({ currentLocationId: locationId });
    if (locationId) {
      get().calculateBudget(locationId);
    }
  },

  calculateBudget: (locationId: string) => {
    const tripData = useTripStore.getState().tripData;
    if (!tripData) return;

    const location = tripData.locations.find((l) => l.id === locationId);
    if (!location) return;

    const budget = calculateLocationBudget(location, tripData);
    const breakdown = get().getBudgetBreakdown(locationId);
    const participantBudgets = get().getParticipantBudgets(locationId);

    set({
      currentLocationId: locationId,
      locationBudget: budget,
      budgetBreakdown: breakdown,
      participantBudgets,
    });
  },

  getBudgetBreakdown: (locationId: string): BudgetBreakdown => {
    const tripData = useTripStore.getState().tripData;
    if (!tripData) {
      return { accommodation: [], transport: [], activities: [] };
    }

    const location = tripData.locations.find((l) => l.id === locationId);
    if (!location) {
      return { accommodation: [], transport: [], activities: [] };
    }

    // Get accommodation breakdown
    const accommodation = location.accommodation.main
      ? [
          {
            name: location.accommodation.main.name,
            cost: location.accommodation.main.pricing?.amount || 0,
            image: location.accommodation.main.mainImageURL,
            duration: location.accommodation.main.duration,
            bookingStatus: location.accommodation.main.bookingStatus,
            id: location.accommodation.main.id.toString(),
          },
        ]
      : [];

    // Get transport breakdown
    const transport = tripData.transport
      .filter((t) => t.toLocationId === locationId)
      .map((transport) => ({
        name: `Flight from ${transport.legs[0]?.departure.location.name} to ${
          transport.legs[transport.legs.length - 1]?.arrival.location.name
        }`,
        cost: transport.total_price,
        image: transport.legs[0]?.provider.logo || "",
        bookingStatus: transport.bookingStatus || "pending",
        id: transport.id.toString(),
      }));

    // Get activities breakdown
    const activities = location.itinerary_items.flatMap((day) =>
      day.items.map((item) => ({
        name: item.name,
        cost: item.pricing || 0,
        image: item.images?.[0] || "",
        participants: item.participants?.filter((p) => p.status === "confirmed")
          .length,
        bookingStatus: item.bookingStatus || "pending",
        id: item.id.toString(),
      }))
    );

    return {
      accommodation,
      transport,
      activities,
    };
  },

  getParticipantBudgets: (locationId: string): ParticipantBudget[] => {
    const tripData = useTripStore.getState().tripData;
    if (!tripData) return [];

    const location = tripData.locations.find((l) => l.id === locationId);
    if (!location) return [];

    // Accommodation cost split among all location participants
    const accommodationCost = location.accommodation.main?.pricing?.total || 0;
    const numParticipants = location.participants.length || 1;
    const accommodationShare = accommodationCost / numParticipants;

    const transportCost = calculateTransportCost(tripData, locationId);
    const transportShare = transportCost;

    // Activities: for each itinerary item, if it has participants inside, split the cost among them
    const activityShares: Record<string, number> = {};
    location.itinerary_items.forEach((day) => {
      day.items.forEach((item) => {
        if (
          item.participants &&
          item.participants.filter((p) => p.status === "confirmed").length > 0
        ) {
          const confirmedParticipants = item.participants.filter(
            (p) => p.status === "confirmed"
          );
          const share = item.pricing || 0;
          confirmedParticipants.forEach((participant) => {
            activityShares[participant.id] =
              (activityShares[participant.id] || 0) + share;
          });
        }
      });
    });

    // Build per-participant budget details
    return location.participants.map((p) => {
      const activitiesShare = activityShares[p.id] || 0;
      return {
        participant: {
          id: p.id,
          name: p.name,
          waytora_avatar_url: p.profile_image || "",
        },
        accommodationShare,
        transportShare,
        activitiesShare,
        total: accommodationShare + transportShare + activitiesShare,
      };
    });
  },

  updateBudgetCategory: (category: keyof LocationBudget, amount: number) => {
    const { currentLocationId, locationBudget } = get();
    if (!currentLocationId) return;

    // Update the local budget state
    const updatedBudget = {
      ...locationBudget,
      [category]: amount,
      total:
        category === "total"
          ? amount
          : locationBudget.total + (amount - locationBudget[category]),
    };

    set({ locationBudget: updatedBudget });

    // Update the trip store
    useTripStore
      .getState()
      .updateLocationBudgetCategory(currentLocationId, category, amount);
  },
}));

// Helper functions for budget calculations
const calculateLocationBudget = (
  location: Location,
  tripData: Trip
): LocationBudget => {
  if (location.origin)
    return {
      accommodation: 0,
      transport: 0,
      activities: 0,
      total: 0,
    };

  const accommodationCost = location.accommodation.main?.pricing?.total || 0;
  const activitiesCost = calculateActivitiesCost(location.itinerary_items);
  const transportCost =
    calculateTransportCost(tripData, location.id) *
    location.participants.length;

  return {
    accommodation: accommodationCost,
    transport: transportCost,
    activities: activitiesCost,
    total: accommodationCost + activitiesCost + transportCost,
  };
};

// Calculate activities cost from itinerary items
const calculateActivitiesCost = (
  items: { date: string; items: ItineraryItem[] }[]
): number => {
  return items.reduce((total, day) => {
    return (
      total +
      day.items.reduce((dayTotal, item) => {
        // If the activity has specific participants, use the count of confirmed participants
        // Otherwise use all participants (this maintains backward compatibility)
        const participantCount = item.participants
          ? item.participants.filter((p) => p.status === "confirmed").length
          : 1; // Default to 1 if no participants specified to maintain backward compatibility
        return dayTotal + (item.pricing || 0) * participantCount;
      }, 0)
    );
  }, 0);
};

const calculateTransportCost = (tripData: Trip, locationId: string): number => {
  return tripData.transport.reduce((total, transport) => {
    // Only include transport where this location is the destination
    if (transport.toLocationId === locationId) {
      return total + (transport.total_price || 0);
    }
    return total;
  }, 0);
};

export default useBudgetStore;
